import React from 'react'
import { useTranslation } from 'react-i18next'
import { Grid } from '@mui/material'
import type { Nil } from '@pbt/pbt-ui-components'

import Typography from '~/components/elements/Typography/Typography'

import { ClinicBrief } from './ClinicBrief'
import type { PreviousMedicalRecordConsent } from './types/clinicTypes'
import { getConsentTexts } from './utils/pmrConsentUtils'

import styles from './PreviousMedicalRecords.module.scss'

interface PreviousMedicalRecordsProps {
  previousMedicalRecordConsent?: PreviousMedicalRecordConsent | Nil
}

const PreviousMedicalRecords: React.FC<PreviousMedicalRecordsProps> = ({
  previousMedicalRecordConsent,
}) => {
  const { t } = useTranslation(['Common', 'TimeTable'])

  // If previousMedicalRecordConsent is not present, show unknown message
  if (!previousMedicalRecordConsent) {
    return (
      <Grid container direction="column">
        <Grid item>
          <Typography.H3 className={styles.titleText}>
            {t('Common:PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
          </Typography.H3>
        </Grid>
        <Grid item className={styles.container}>
          <Typography.Paragraph>
            {t('Common:PMR_CONSENT.PREVIOUS_PRACTICE_UNKNOWN')}
          </Typography.Paragraph>
        </Grid>
      </Grid>
    )
  }

  const { consent, previousClinicContacts } = previousMedicalRecordConsent

  const { consentText, descriptionText, showConsentLine } = getConsentTexts(
    consent,
    t,
    t('TimeTable:PMR_CONSENT.CONSENT_NULL_DESCRIPTION'),
  )

  const showClinics = consent !== null && previousClinicContacts?.length > 0

  return (
    <Grid container direction="column">
      <Grid item>
        <Typography.H3 className={styles.titleText}>
          {t('Common:PMR_CONSENT.PREVIOUS_MEDICAL_RECORDS_TITLE')}
        </Typography.H3>
      </Grid>
      <Grid container className={styles.container} gap={1}>
        <Grid item>
          {showConsentLine && (
            <Typography.H3
              className={
                consent === false ? styles.consentTextError : styles.consentText
              }
            >
              {consentText}
            </Typography.H3>
          )}
          <Typography.Paragraph>{descriptionText}</Typography.Paragraph>
        </Grid>
        {showClinics && (
          <Grid container direction="column" spacing={1}>
            {previousClinicContacts.map((clinic) => (
              <Grid item key={clinic.id}>
                <ClinicBrief foldable clinic={clinic} defaultExpanded={false} />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

export default PreviousMedicalRecords
